/**
 * Preferences review screen — summary per type with edit entry.
 *
 * Belongs here: review layout, out-of-sync autosubmit, Save → hub.
 * Does not belong here: flow rules.
 */
import { useEffect, useMemo, useState } from 'react'
import { PrimaryButton } from '../../../../../components/execution/PrimaryButton'
import { SecondaryButton } from '../../../../../components/execution/SecondaryButton'
import { usePreferencesContext } from '../flow-engine/PreferencesProvider'
import { useExpertReviewNavigation } from '../../navigation/expertReviewNavigation'
import { EXPERT_REVIEW_CAME_FROM } from '../../navigation/expertReviewNavigation'
import { getBottomPadding } from '../../../../../utils/sessionStorage'
import { EventNames } from '../../../../../types/misc.types'
import { expertReviewSafeAreaTopStyle } from '../../shared/expertReviewSafeArea'
import { getPreferenceTypeIcon } from '../ui/getPreferenceTypeIcon'
import { getGroupReviewSummaryLines, groupHasMultiSelectReview } from '../../question-utils/preferencesGroupReviewSummary'
import type { FundPreferenceType, PreferencesAnswersMap, PreferenceQuestionGroup } from '../setup/types'
import ThreeLineLoading from '../../../../../components/ui-components/loading/ThreeLineLoading'
import { ImageAvailability } from '../../shared/types/common.types'
import { ExpertReviewTopBar } from '../../shared/components/ExpertReviewTopBar'
import { ExpertReviewEventIds, ExpertReviewScreen } from '../../analytics/expertReviewEvents'
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { CONFIRMED_ICON_SVG } from '../../shared/components/RadioOptionRow'
import { CONFIRM_ACTION_DELAY_MS } from '../../shared/transitions/contentTransitionTimings'
import {
  DirectionalSlideSwap,
  SlideDirection
} from '../../shared/transitions/DirectionalSlideSwap'
import { lightImpactVibrate, trackEvent } from '../../../../../utils/appBridge'

const NEW_CATEGORY_DETECTED_LABEL = 'New category detected'
function PreferenceTypeReviewRow({
  preferenceType,
  group,
  answers,
  onEdit,
  testLocator,
  testLocatorEdit
}: {
  preferenceType: FundPreferenceType
  group: PreferenceQuestionGroup | undefined
  answers: PreferencesAnswersMap
  onEdit: () => void
  testLocator: string
  testLocatorEdit: string
}) {
  const { preferenceTypeIconAvailabilityById } = usePreferencesContext()
  const PreferenceTypeIcon = getPreferenceTypeIcon(
    preferenceType.id,
    preferenceTypeIconAvailabilityById[preferenceType.id] ?? ImageAvailability.UNKNOWN
  )
  const reviewLines = useMemo(() => {
    const hasAnsweredAnyQuestionForGroup = (group?.questions ?? []).some((question) => answers[question.id] != null)

    // Multi-select groups use dedicated summary builder instead of fund count line
    if (group != null && groupHasMultiSelectReview(group)) {
      const summaryLines = getGroupReviewSummaryLines(group, answers)
      if (group.isNewPreference === true && !hasAnsweredAnyQuestionForGroup) {
        return [NEW_CATEGORY_DETECTED_LABEL, ...summaryLines]
      }
      return summaryLines
    }

    const hasFundCountInReviewResolver = (group?.reviewResolver ?? []).some(
      (resolverItem) => resolverItem.showFundCount === true
    )
    const fundCountQuestionId = (group?.reviewResolver ?? []).find(
      (resolverItem) => resolverItem.showFundCount === true
    )?.questionId
    const hasAnswerForFundCountQuestion =
      fundCountQuestionId != null && answers[fundCountQuestionId] != null

    const lines = hasFundCountInReviewResolver && hasAnswerForFundCountQuestion
      ? []
      : [
        preferenceType.fundCount === 0
          ? 'No investments'
          : `${preferenceType.fundCount} ${preferenceType.fundCount === 1 ? 'fund' : 'funds'}`
      ]

    if (group == null) {
      return lines
    }

    if (group?.isNewPreference === true && !hasAnsweredAnyQuestionForGroup) {
      lines.push(NEW_CATEGORY_DETECTED_LABEL)
    } else {
      // Resolver-driven lines (fund names, selections, etc.)
      lines.push(...getGroupReviewSummaryLines(group, answers, preferenceType.fundCount))
    }

    return lines
  }, [group, answers, preferenceType.fundCount])

  return (
    <div className="flex items-start gap-4 bg-white p-4 rounded-full">
      <span
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center"
        aria-hidden
      >
        <PreferenceTypeIcon className="h-6 w-6" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-[500] text-[#232323]" test-locator={testLocator}>{preferenceType.name}</p>
        {reviewLines.length > 0 &&
          reviewLines.map((line, i) => (
            <p
              key={i}
              className={`mt-1 first:mt-0 ${line === NEW_CATEGORY_DETECTED_LABEL
                ? 'text-[12px] font-[500] text-[#CC5C2D]'
                : 'text-[12px] font-[400] text-[#6A6E6E]'
                }`}
            >
              {line}
            </p>
          ))}
      </div>
      <div
        onClick={onEdit}
        className="shrink-0 cursor-pointer text-[14px] font-[500] text-[#1470CC]"
        test-locator={testLocatorEdit}
      >
        Edit
      </div>
    </div>
  )
}

export default function AllocationPreferencesReviewPage() {
  const navigation = useExpertReviewNavigation()
  const {
    questionnaireData,
    preferenceTypes,
    committedAnswers,
    committedAnswersVersion,
    isAnswersVersionOutOfSync,
    beginEditOne,
    beginEditAll,
    submitPreferencesAndStatusRefresh,
    isPreferenceTypesLoading,
    isPreferenceTypesError,
    markReachedReview
  } = usePreferencesContext()

  // Any entry to full preferences review — hub can resume here later
  useEffect(() => {
    markReachedReview()
  }, [markReachedReview])

  // When answers version is out of sync — silent POST + status (first land + edit→back without remount)
  useEffect(() => {
    const questionnaireId = questionnaireData?.questionnaireId ?? ''
    if (!questionnaireId || Object.keys(committedAnswers).length === 0) return
    if (!isAnswersVersionOutOfSync) return
    void submitPreferencesAndStatusRefresh(
      {
        questionnaireId,
        answers: committedAnswers,
        answersVersion: committedAnswersVersion
      },
      { silent: true }
    ).catch(() => {
      // Stay out of sync — Save still POSTs when needed
    })
  }, [
    committedAnswersVersion,
    committedAnswers,
    isAnswersVersionOutOfSync,
    questionnaireData?.questionnaireId,
    submitPreferencesAndStatusRefresh
  ])

  const openedFromHub =
    navigation.navigationState.cameFrom === EXPERT_REVIEW_CAME_FROM.HUB
  // Hub pending: back + menu + Save. After questionnaire: menu + Continue (menu without back is ok).
  const showBack = openedFromHub
  const primaryCtaLabel = 'Save preferences'
  const [isConfirming, setIsConfirming] = useState(false)
  const showReadyReview = !isPreferenceTypesLoading && !isPreferenceTypesError

  useEffect(() => {
    if (!showReadyReview) return
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.PreferencesReview }
    })
  }, [showReadyReview])

  const handleBack = () => navigation.goBackFromPreferencesReview()

  const handleEditPreferenceType = (preferenceTypeId: string, preferenceTypeName: string) => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: ExpertReviewScreen.PreferencesReview,
        eventId: ExpertReviewEventIds.PreferencesReviewEdit,
        buttonLabel: preferenceTypeName
      }
    })
    beginEditOne(preferenceTypeId)
  }

  const handleEditAll = () => {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: ExpertReviewScreen.PreferencesReview,
        eventId: ExpertReviewEventIds.PreferencesReviewEditAll,
        buttonLabel: 'Edit all preferences'
      }
    })
    beginEditAll()
  }

  const handlePrimaryCta = () => {
    if (isConfirming) return
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: ExpertReviewScreen.PreferencesReview,
        eventId: ExpertReviewEventIds.PreferencesSave,
        buttonLabel: primaryCtaLabel
      }
    })
    setIsConfirming(true)
    // Match question-flow confirm: show tick, then submit if out of sync and leave on success
    window.setTimeout(() => {
      void (async () => {
        try {
          const questionnaireId = questionnaireData?.questionnaireId ?? ''
          if (
            questionnaireId &&
            Object.keys(committedAnswers).length > 0 &&
            isAnswersVersionOutOfSync
          ) {
            await submitPreferencesAndStatusRefresh({
              questionnaireId,
              answers: committedAnswers,
              answersVersion: committedAnswersVersion
            })
          }
          navigation.goToHub({ replace: true })
        } catch {
          // Toast already shown in submitPreferencesAndStatusRefresh — stay on review
          setIsConfirming(false)
        }
      })()
    }, CONFIRM_ACTION_DELAY_MS)
  }

  if (isPreferenceTypesLoading) {
    return (
      <ThreeLineLoading />
    )
  }

  if (isPreferenceTypesError) {
    return (
      <div
        className="h-screen bg-white px-5 py-6 flex flex-col"
        style={{
          ...expertReviewSafeAreaTopStyle(),
          paddingBottom: `calc(${getBottomPadding()}px + 16px)`
        }}
      >
        <header className="shrink-0">
          <ExpertReviewTopBar
            onBack={handleBack}
            menu="preferences"
            analyticsScreen={ExpertReviewScreen.PreferencesReview}
            testLocatorBack="preferences-review-error-back"
            testLocatorMenu="preferences-review-error-menu"
          />
        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-600">Failed to load preference types</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="h-screen bg-white flex flex-col min-h-0"
      style={expertReviewSafeAreaTopStyle()}
    >
      <header className="flex-shrink-0 px-5">
        <ExpertReviewTopBar
          onBack={showBack ? handleBack : undefined}
          menu="preferences"
          analyticsScreen={ExpertReviewScreen.PreferencesReview}
          testLocatorBack="preferences-review-back"
          testLocatorMenu="preferences-review-menu"
        />
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-5">
        <DirectionalSlideSwap contentKey="preferences-review" direction={SlideDirection.Forward}>
          <h1 className="pt-4 text-[28px] font-[400] leading-[130%] text-[#232323]" test-locator="preferences-review-title">
            Review your inputs
          </h1>
          <p className="pt-3 text-[13px] font-[400] leading-[150%] text-[#6A6E6E] w-[95%]" test-locator="preferences-review-description">
            This helps our experts understand which funds they can make recommendations on, and which ones to leave unchanged.
          </p>
          <section className="mt-6 flex flex-col py-1 rounded-[16px] border border-[#F0F4F7]">
            {preferenceTypes.map((preferenceType) => {
              const group = questionnaireData?.questionGroups?.find((g) => g.groupId === preferenceType.id)
              return (
                <PreferenceTypeReviewRow
                  key={preferenceType.id}
                  preferenceType={preferenceType}
                  group={group}
                  answers={committedAnswers}
                  onEdit={() => handleEditPreferenceType(preferenceType.id, preferenceType.name)}
                  testLocator={`preferences-review-type-${preferenceType.id}`}
                  testLocatorEdit={`preferences-review-type-${preferenceType.id}-edit`}
                />
              )
            })}
          </section>
          <div className="pt-4 pb-10">
            <SecondaryButton
              testLocator="preferences-review-edit-all"
              label="Edit all preferences"
              onClick={handleEditAll}
              secondaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
            />
          </div>
        </DirectionalSlideSwap>
      </div>

      <div
        style={{ paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
        className="shrink-0 px-5 pt-5 border-t border-[#F0F4F7]"
      >
        <PrimaryButton
          testLocator="preferences-review-continue"
          label={primaryCtaLabel}
          onClick={handlePrimaryCta}
          primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px]"
        >
          {isConfirming ? (
            <span className="flex items-center justify-center gap-2">
              <RenderHTML innerHTMLText={CONFIRMED_ICON_SVG} />
            </span>
          ) : null}
        </PrimaryButton>
      </div>
    </div>
  )
}
